import { useState } from "react"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import {
  Type,
  Palette,
  Users2,
  Eye,
  Check,
  Settings2,
} from "lucide-react"
import { Switch } from "@/clientWeb/components/ui/switch"
import { Badge } from "@/clientWeb/components/ui/badge"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"



// `label` is a translation key, resolved at render.
const COLORS = [
  { value: "#ffffff", label: "transcription.colors.classicWhite", border: "border-stroke-100 dark:border-border" },
  { value: "#facc15", label: "transcription.colors.highContrast", border: "border-transparent" },
  { value: "#10b981", label: "transcription.colors.softEmerald", border: "border-transparent" },
  { value: "#3b82f6", label: "transcription.colors.skyBlue", border: "border-transparent" },
  { value: "#ef4444", label: "transcription.colors.coralRed", border: "border-transparent" }
]

export default function Transcription() {
  const t = useTranslation()
  const [fontSize, setFontSize] = useState(20)
  const [captionColor, setCaptionColor] = useState("#ffffff")
  const [publishSummary, setPublishSummary] = useState(true)

  return (
    <div className="max-w-6xl mx-auto space-y-4 animate-in fade-in duration-700">

      {/* Header Section */}
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-extrabold text-foreground tracking-tight">
          {t("transcription.title")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("transcription.subtitle")}
        </p>
      </div>

      {/* 1. TOP SECTION: Intelligence Hub (Refined White Style) */}
      <div className="relative shadow-sm overflow-hidden rounded-2xl bg-card border border-stroke-100 dark:border-border dark:border-border p-4">
        {/* Subtle Decorative Element */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/5 rounded-full blur-3xl" />
        {/* <Sparkles className="absolute top-6 right-6 w-8 h-8 text-primary/20" /> */}

        <div className="relative z-10 flex  md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-card text-primary border border-stroke-100 dark:border-border dark:border-border shadow-sm">
              <Users2 className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-md font-bold text-foreground tracking-tight">{t("transcription.intelligenceHub")}</h3>
              <p className="text-muted-foreground text-sm max-w-md mt-1 text-xs font-medium">
                {t("transcription.intelligenceHubDesc")}
              </p>
            </div>
          </div>

          {/* <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-stroke-100 dark:border-border"> */}

          <Switch
            checked={publishSummary}
            onCheckedChange={(val) => {
              setPublishSummary(val)
              toast.success({ title: val ? t("transcription.summariesEnabled") : t("transcription.summariesDisabled"), description: val ? t("transcription.summariesEnabledDesc") : t("transcription.summariesDisabledDesc") })
            }}
            className="data-[state=checked]:bg-primary"
          />
          {/* </div> */}
        </div>
      </div>

      {/* 2. BOTTOM SECTION: Visual Workspace */}
      <div className="grid lg:grid-cols-12 gap-8 items-start">

        {/* Left: Styling Controls (Pure White implementation) */}
        <div className="lg:col-span-5 space-y-6">
          <div className="p-8 rounded-3xl bg-card border border-stroke-100 dark:border-border dark:border-border shadow-xs space-y-4">
            <div className="flex items-center gap-3 border-b border-slate-50 dark:border-border pb-5">
              <Settings2 className="w-5 h-5 text-primary" />
              <h4 className="font-bold text-foreground">{t("transcription.captionStyling")}</h4>
            </div>

            {/* Font Size Control */}
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Type className="w-4 h-4 text-slate-400" />
                  <label className="text-sm font-semibold">{t("transcription.typography")}</label>
                </div>
                <Badge variant="secondary" className="font-mono bg-slate-100 text-slate-600 border-none px-3">
                  {fontSize}px
                </Badge>
              </div>
              <input
                type="range"
                min={14}
                max={32}
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-100 dark:bg-secondary/50 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-[10px] font-semibold">
                <span>{t("transcription.minimal")}</span>
                <span>{t("transcription.maximum")}</span>
              </div>
            </div>

            {/* Color Palette Control */}
            <div className="space-y-5">
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-slate-400" />
                <label className="text-xs font-semibold">{t("transcription.visualColor")}</label>
              </div>
              <div className="flex flex-wrap gap-4">
                {COLORS.map((color) => (
                  <button
                    key={color.value}
                    aria-label={t(color.label)}
                    aria-pressed={captionColor === color.value}
                    onClick={() => { setCaptionColor(color.value); toast.info({ title: t("transcription.colorUpdated"), description: t("transcription.colorSet") }) }}
                    className={`group relative w-12 h-12 rounded-xl transition-all duration-300 hover:shadow-md border-2 ${captionColor === color.value
                        ? "border-primary ring-4 ring-primary/5"
                        : "border-stroke-100 dark:border-border"
                      }`}
                    style={{ backgroundColor: color.value }}
                  >
                    {captionColor === color.value && (
                      <Check className="w-5 h-5 text-slate-900 dark:text-foreground/80 absolute inset-0 m-auto animate-in zoom-in" />
                    )}
                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-slate-500">
                      {t(color.label)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right: Preview Stage (Cinema Mockup) */}
        <div className="lg:col-span-7">
          <div className="sticky top-8 space-y-4">
            <div className="flex items-center justify-between px-4">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                <span className="text-sm font-semibold">{t("transcription.realtimePreview")}</span>
              </div>
            </div>

            <div className="relative aspect-video rounded-3xl overflow-hidden border border-white dark:border-border shadow-2xl group">
              {/* Mock Video Backdrop */}
              <div className="absolute inset-0 bg-slate-800">
                <div className="absolute inset-0 opacity-40 bg-[url('https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&q=80&w=1000')] bg-cover bg-center grayscale" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                {/* <div className="absolute inset-0 flex items-center justify-center">
                  <MonitorPlay className="w-16 h-16 text-white/10 group-hover:scale-110 transition-transform duration-500" />
                </div> */}
              </div>

              {/* Live Caption Display */}
              <div className="absolute inset-0 flex flex-col items-center justify-end pb-4 px-10">
                <div
                  className="transition-all duration-300 ease-out py-3 px-8 rounded-2xl backdrop-blur-md bg-black/50 shadow-xl border border-white/10 text-center leading-snug"
                  style={{
                    fontSize: `${fontSize}px`,
                    color: captionColor,
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  {t("transcription.previewText")}
                </div>
                {/* <div className="mt-4 flex items-center gap-2 opacity-50">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[9px] font-black text-white uppercase tracking-widest">Live Transcribing</span>
                </div> */}
              </div>

              {/* Status Overlay */}
              {/* <div className="absolute top-6 left-8 flex items-center gap-2">
                <div className="px-2 py-0.5 rounded-md bg-red-500 text-[9px] font-bold text-white uppercase tracking-wider">Live</div>
                <div className="px-2 py-0.5 rounded-md bg-card/20 backdrop-blur-sm text-[9px] font-mono text-white">01:42:05</div>
              </div> */}
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}